import * as React from 'react';
import { createFileRoute, redirect, useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PageTitleContext } from '../../__root';
import { PoiAdminDashboard } from '../../../features/pois/components/poi-admin-dashboard';
import { CreatePoiDialog } from '../../../features/pois/components/create-poi-dialog';
import { DeletePoiDialog } from '../../../features/pois/components/delete-poi-dialog';
import type { Poi } from '@ooh/contracts';
import { poiQueries } from '../../../features/pois/api/queries';
import type { WithCallWithAuth } from '../../../features/pois/api/queries';
import { useEffectiveAuth } from '../../../features/pois/api/mutations';
import { TransportImportPanel } from '../../../features/transport-layers/components/transport-import-panel';
import { AdminBoundaryImportPanel } from '../../../features/admin-boundaries/components/admin-boundary-import-panel';
import { validateAdminPoisSearch } from './validate-search';
import { SYSTEM_ROLE_CODE } from '../../../features/permissions/lib/role-codes';

export type { AdminPoisSearch } from './validate-search';

// ─── Route ───────────────────────────────────────────────────

export const Route = createFileRoute('/admin/pois/')({
  validateSearch: validateAdminPoisSearch,
  beforeLoad: ({ context }) => {
    const user = context.auth.user;
    if (!user || !user.roles.includes(SYSTEM_ROLE_CODE)) {
      throw redirect({ to: '/media-units' });
    }
  },
  component: AdminPoisRoute,
});

function AdminPoisRoute() {
  const { auth } = useRouteContext({ from: '__root__' });
  return <AdminPoisPage auth={auth} />;
}

// ─── Page (exported for testing) ─────────────────────────────

export function AdminPoisPage({ auth }: { auth?: WithCallWithAuth } = {}) {
  const effectiveAuth = useEffectiveAuth(auth);
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t, i18n } = useTranslation('map');

  const [createOpen, setCreateOpen] = React.useState(false);
  const [poiToDelete, setPoiToDelete] = React.useState<Poi | null>(null);

  React.useEffect(() => {
    setPageTitle(t('admin.pois.pageTitle'));
  }, [setPageTitle, t]);

  const { data: categoriesData } = useQuery(poiQueries.categories(effectiveAuth));
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useQuery({ ...poiQueries.stats(effectiveAuth), refetchOnWindowFocus: false });

  const lastImportLabel = React.useMemo(() => {
    if (statsLoading) return null;
    if (!stats?.lastImportedAt) return t('admin.pois.stats.neverImported');
    return new Date(stats.lastImportedAt).toLocaleString(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, [stats, statsLoading, t, i18n.language]);

  return (
    <>
      <PoiAdminDashboard
        stats={stats}
        statsLoading={statsLoading}
        lastImportLabel={lastImportLabel}
        auth={effectiveAuth}
        onDelete={(poi) => setPoiToDelete(poi)}
        onCreate={() => setCreateOpen(true)}
        onRefresh={() => void refetchStats()}
        transportImportPanel={<TransportImportPanel auth={effectiveAuth} />}
        adminBoundaryImportPanel={<AdminBoundaryImportPanel auth={effectiveAuth} />}
      />

      <CreatePoiDialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) void refetchStats();
        }}
        categories={categories}
        auth={effectiveAuth}
      />

      <DeletePoiDialog
        poi={poiToDelete}
        open={poiToDelete != null}
        onOpenChange={(open) => {
          if (!open) {
            setPoiToDelete(null);
            void refetchStats();
          }
        }}
        auth={effectiveAuth}
      />
    </>
  );
}
